import React from 'react';

/**
 * Wraps React.lazy() with automatic retry and page-reload fallback for
 * failed dynamic imports (chunk load errors).
 *
 * After a deployment, Vite generates new chunk filenames (content hashes).
 * If the browser has a cached HTML/JS bundle referencing old chunk names,
 * the dynamic import fails with "Failed to fetch dynamically imported module".
 *
 * This wrapper:
 *  1. Retries the import once after a short delay.
 *  2. If the retry also fails, forces a full page reload so the browser
 *     fetches the new index.html with correct chunk references.
 *  3. Uses a sessionStorage counter (max 1 reload) to prevent infinite loops.
 *     The counter is never cleared by successful imports from other modules —
 *     it resets only when the session ends (tab close) or via the 5-minute
 *     expiry, ensuring one module's success can't re-enable reloads for
 *     another module that's still failing.
 */

const RELOAD_KEY = 'chunk-reload-count';
const RELOAD_TS_KEY = 'chunk-reload-ts';
const MAX_RELOADS = 1;
/** After this many ms the reload counter resets, allowing fresh reloads
 *  for a subsequent deployment. */
const RELOAD_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('loading chunk') ||
      msg.includes('loading css chunk') ||
      msg.includes('dynamically imported module')
    );
  }
  return false;
}

/** Returns current reload count, resetting if the expiry window has passed. */
function getReloadCount(): number {
  const ts = Number(sessionStorage.getItem(RELOAD_TS_KEY) || '0');
  if (ts && Date.now() - ts > RELOAD_EXPIRY_MS) {
    // Expired — reset so a new deployment can trigger the reload again
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_TS_KEY);
    return 0;
  }
  return Number(sessionStorage.getItem(RELOAD_KEY) || '0');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic over arbitrary component prop shapes, mirroring React.lazy's own signature
export default function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    importFn().catch(() => {
      // Retry once after a short delay
      return new Promise<{ default: T }>((resolve, reject) => {
        setTimeout(() => {
          importFn()
            .then(resolve)
            .catch((retryError: unknown) => {
              if (isChunkLoadError(retryError)) {
                const count = getReloadCount();
                if (count < MAX_RELOADS) {
                  sessionStorage.setItem(RELOAD_KEY, String(count + 1));
                  sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
                  window.location.reload();
                  // Return a never-resolving promise to avoid rendering stale UI
                  return;
                }
                // Already reloaded max times — let error propagate to ErrorBoundary
              }
              reject(retryError);
            });
        }, 1000);
      });
    }),
  );
}
